import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import jobBoardApi from '../api/job-board.js';
// eslint-disable-next-line import/no-cycle
import { setAuthState } from '../composables/auth';

/**
 * EN-621 — the queue of external applications waiting on a "Did you apply?" answer.
 *
 * Deliberately sessionStorage rather than localStorage or a server table. The spec's
 * "no response / closes app -> treat as Not yet" falls out for free when the queue dies with
 * the tab, and sessionStorage still survives a refresh, SPA navigation and mobile Safari's
 * same-tab back-navigation, all of which must keep the prompt armed. A server-side record
 * written at redirect time would outlive the tab and re-prompt on the next visit instead.
 */
const STORAGE_KEY = 'applyConfirmationQueue';

/** The prompt is only offered for two hours after the redirect. */
export const WINDOW_MS = 2 * 60 * 60 * 1000;

export const useApplyConfirmationStore = defineStore('apply-confirmation', () => {
	const { getUser, setUser } = setAuthState();

	const queue = ref([]);

	const persist = () => {
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queue.value));
		} catch {
			// sessionStorage may be unavailable (private mode / quota) — persistence is best-effort
		}
	};

	const restore = () => {
		try {
			const stored = sessionStorage.getItem(STORAGE_KEY);
			queue.value = stored ? JSON.parse(stored) ?? [] : [];
		} catch {
			queue.value = [];
		}
	};

	restore();

	const isFresh = (entry) => Date.now() - entry.redirectedAt < WINDOW_MS;

	const prune = () => {
		const fresh = queue.value.filter(isFresh);

		if (fresh.length !== queue.value.length) {
			queue.value = fresh;
			persist();
		}
	};

	/**
	 * The oldest entry that is both inside the window and known to have taken the seeker away.
	 *
	 * `departed` is the popup-blocker guard: if `target=_blank` was blocked the seeker never
	 * left, and prompting them about a job they never saw is worse than missing one.
	 */
	const current = computed(() => queue.value.find((entry) => entry.departed && isFresh(entry)) ?? null);

	/**
	 * Upsert by job id, refreshing the timestamp — clicking through to the same job twice
	 * before returning must show one prompt, dated from the last redirect.
	 */
	const enqueue = (job) => {
		const providerJobId = job?.provider_job_id;

		if (!providerJobId) {
			return;
		}

		prune();

		const entry = {
			providerJobId,
			redirectedAt: Date.now(),
			departed: false,
		};

		const index = queue.value.findIndex((item) => item.providerJobId === providerJobId);

		if (index === -1) {
			queue.value.push(entry);
		} else {
			// Keep the original slot: prompts are shown in the order the jobs were opened.
			queue.value.splice(index, 1, { ...queue.value[index], ...entry });
		}

		persist();
	};

	/** The seeker left the tab, so everything queued so far really did open. */
	const markDeparted = () => {
		if (queue.value.length === 0) {
			return;
		}

		queue.value = queue.value.map((entry) => ({ ...entry, departed: true }));
		persist();
	};

	const remove = (providerJobId) => {
		queue.value = queue.value.filter((entry) => entry.providerJobId !== providerJobId);
		persist();
	};

	/**
	 * Called from clearUser() on logout. The queue is per tab, not per account, so without
	 * this the next person to sign in on the same tab would be asked about — and could
	 * confirm onto their own account — a job the previous seeker opened.
	 */
	const clear = () => {
		queue.value = [];
		persist();
	};

	/**
	 * "Not yet" — no state change at all, so the next redirect for this job re-arms the prompt.
	 */
	const dismiss = (providerJobId) => {
		remove(providerJobId);
	};

	/**
	 * The confirmation wrote a sent Job Tracker row but answers with no user, so the cached
	 * `has_sent_application` is set here; otherwise the EN-638 exit-intent modal would still ask
	 * for a "first application" until the next page load (EN-1185).
	 */
	const markApplicationSent = () => {
		const user = getUser();

		if (user) {
			setUser({ ...user, has_sent_application: true });
		}
	};

	/**
	 * "Yes, add to JobTrackerAI". The entry is dropped either way: a 422 means the posting was
	 * force-deleted between the redirect and the answer, which is not worth re-asking about.
	 */
	const confirm = async (providerJobId) => {
		remove(providerJobId);

		try {
			await jobBoardApi.confirmApplication(providerJobId);
			markApplicationSent();

			return true;
		} catch {
			return false;
		}
	};

	return {
		queue,
		current,
		enqueue,
		markDeparted,
		dismiss,
		confirm,
		prune,
		clear,
	};
});
